/**
 * The sidebar's workspace writes (cards 5.2 / 5.3) with their side effects in
 * one place: the docscope / scopeId write (documentsScopes.js,
 * documentsActions.js), the tree bump that re-reads every view, open tabs
 * re-pointed at their document's workspace, and the notification. Callers
 * (switcher, modals, Move to…) stay layout only.
 */
import i18n from '../../i18n';
import {
  createScope,
  deleteScope,
  renameScope,
  reorderScopes,
} from '../../extensions/bundled/documents/documentsScopes';
import { moveToTarget } from '../../extensions/bundled/documents/documentsActions';
import { bumpDocumentsTree, useDocumentsUiStore } from '../../extensions/bundled/documents/documentsUiStore';
import { relocateDocumentTabs } from '../document-editor/relocateTabs';
import { useNotificationStore } from '../../store/notificationStore';
import { errorMessage } from '../../utils/errorMessage';
import { workspaceKey } from './scopeChoices';

const notes = () => useNotificationStore.getState();

/** A store error's words: its stable code when the store gave one, else the fallback. */
function failure(err, fallbackKey) {
  const code = err?.code ? `docStore.errors.${err.code}` : null;
  return code && i18n.exists(code) ? i18n.t(code) : errorMessage(err, i18n.t(fallbackKey));
}

/** Creates a workspace and switches the sidebar to it. Resolves it, or null on failure. */
export async function createWorkspace(fields) {
  try {
    const ws = await createScope(fields);
    bumpDocumentsTree();
    useDocumentsUiStore.getState().setScopeKey(workspaceKey(ws.id));
    return ws;
  } catch (err) {
    notes().error(failure(err, 'docSidebar.workspaces.errors.create'), i18n.t('docSidebar.workspaces.errors.title'));
    return null;
  }
}

/** Renames / re-icons / re-colours one. Resolves true when it was written. */
export async function updateWorkspace(id, { name, icon, color }) {
  try {
    await renameScope(id, name, { icon, color });
    bumpDocumentsTree();
    return true;
  } catch (err) {
    notes().error(failure(err, 'docSidebar.workspaces.errors.rename'), i18n.t('docSidebar.workspaces.errors.title'));
    return false;
  }
}

export async function reorderWorkspaces(orderedIds) {
  try {
    await reorderScopes(orderedIds);
    bumpDocumentsTree();
  } catch (err) {
    notes().error(failure(err, 'docSidebar.workspaces.errors.reorder'), i18n.t('docSidebar.workspaces.errors.title'));
  }
}

/**
 * Deletes a workspace; its documents move to Personal first (never deleted).
 * The sidebar leaves the deleted tree for Personal.
 */
export async function removeWorkspace(ws) {
  try {
    const { moved } = await deleteScope(ws.id);
    const ui = useDocumentsUiStore.getState();
    if (ui.scopeKey === workspaceKey(ws.id)) ui.setScopeKey('personal');
    bumpDocumentsTree();
    await relocateDocumentTabs();
    notes().success(
      i18n.t('docSidebar.workspaces.deleted', { name: ws.name, count: moved }),
      i18n.t('docSidebar.workspaces.deletedTitle')
    );
    return true;
  } catch (err) {
    notes().error(failure(err, 'docSidebar.workspaces.errors.delete'), i18n.t('docSidebar.workspaces.errors.title'));
    return false;
  }
}

/**
 * Moves `node` (with its whole subtree) to the top of another personal tree:
 * a workspace id, or `null` for Personal. `fromScope` is the tree it is in.
 */
export async function moveToWorkspace(node, fromScope, scopeId, name) {
  try {
    await moveToTarget(node, { kind: 'workspace', scopeId: scopeId ?? null }, fromScope, []);
    bumpDocumentsTree();
    await relocateDocumentTabs();
    notes().success(
      i18n.t('docSidebar.workspaces.moved', { title: node.name || i18n.t('docSidebar.untitled'), name }),
      i18n.t('docSidebar.workspaces.movedTitle')
    );
    return true;
  } catch (err) {
    notes().error(failure(err, 'docSidebar.errors.move'), i18n.t('docSidebar.menu.moveTo'));
    return false;
  }
}
